import React from 'react';
import type { RowSelectionAction } from '@europrocurement/flexy-datatable/src/components/FlexyDatatableSelectionAction/FlexyDatatableSelectionAction';
import type { PreFilterDatatableList } from '@europrocurement/flexy-datatable/src/components/DatatableHeader/DatatableHeader';
import type { LimitAccessFilterApiMultipleFactureOpenSearch } from '@europrocurement/l2d-modules/src/modules/Factures/hooks/useFactureApiHook';
import useFactureColumns from '../modules/Factures/hooks/useFactureColumns';
import useFactureFilters, { FactureStatus } from '../modules/Factures/hooks/useFactureFilters';

interface UseFactureTableOptions extends LimitAccessFilterApiMultipleFactureOpenSearch {
    customActionsColumn?: React.ReactNode;
    customRowActions?: RowSelectionAction[];
}

interface UseFactureTableReturn {
    columns: ReturnType<typeof useFactureColumns>['columnsFront'];
    filters: ReturnType<typeof useFactureFilters>['factureFiltersFront'];
    preFilters: PreFilterDatatableList;
    rowActions: RowSelectionAction[];
}

const useFactureTable = ({
    prescriberIdRef,
    ddmIdFilter,
    customActionsColumn,
    customRowActions,
}: UseFactureTableOptions): UseFactureTableReturn => {
    const { columnsFront } = useFactureColumns();
    const { factureFiltersFront } = useFactureFilters({ prescriberIdRef, ddmIdFilter });

    const defaultRowActions: RowSelectionAction[] = customRowActions || [];

    const columns = [...columnsFront];

    if (customActionsColumn) {
        columns.push({
            label: 'Actions',
            displayLabel: false,
            render: () => customActionsColumn,
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        });
    }

    const preFilters: PreFilterDatatableList = {
        label: 'Filtrer par statuts',
        items: [
            {
                label: 'Tout',
                filters: [],
            },
            {
                label: 'A régler',
                filters: [{ field: 'status', value: FactureStatus.TO_PAY }],
            },
            {
                label: 'En retard',
                filters: [{ field: 'status', value: FactureStatus.LATE }],
            },
            {
                label: 'Réglée',
                filters: [{ field: 'status', value: FactureStatus.PAYED }],
            },
        ],
    };

    return { columns, filters: factureFiltersFront, preFilters, rowActions: defaultRowActions };
};

export default useFactureTable;
